import React from 'react'
import { Link, useNavigate } from 'react-router-dom'

import CareFlow from './CareFlow'
import { Container } from './sections'
import { getConditionBySlug } from './conditionsData'
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  MessageCircle,
  PhoneCall,
  ShieldCheck,
  Stethoscope,
} from './icons'
import { isLoggedIn } from 'src/consumer/services/patientProspectService'

/*
 * ConditionPage
 *
 * Renders a single condition's dedicated page. The content itself
 * lives in conditionsData.jsx; this component is purely presentation +
 * calls-to-action.
 *
 * Primary CTAs:
 *   - "Book an expert consultation" -> opens the Connect-with-our-team
 *     modal (the same one triggered from the header phone pill).
 *     Framed as a curated hand-off to a leading psychiatrist in this
 *     condition, not a self-serve calendar.
 *   - "Start the self-assessment" -> signed-in visitors go to the
 *     assessment immediately; anonymous visitors go through signup with
 *     explicit assessment intent so the post-signup flow can create the
 *     patient-prospect role before returning to the screener.
 *
 * Props:
 *   - slug: string (from the URL param)
 *   - onContactClick({ mode }): called when any CTA wants to open the
 *     contact modal. Parent decides call vs. whatsapp.
 */

const ACCENT_TO_CSS = {
  indigo: 'cn-accent-indigo',
  violet: 'cn-accent-violet',
  teal: 'cn-accent-teal',
  amber: 'cn-accent-amber',
  rose: 'cn-accent-rose',
  sky: 'cn-accent-sky',
  slate: 'cn-accent-slate',
}

export default function ConditionPage({ slug, onContactClick }) {
  const navigate = useNavigate()
  const condition = getConditionBySlug(slug)

  if (!condition) {
    return (
      <main id="main" className="as-section as-section-alt">
        <Container>
          <div className="as-max-w-3">
            <h1 className="as-h2">Condition not found</h1>
            <p className="as-lede">
              Explore the full list of conditions we support, or return to the home page.
            </p>
            <div className="as-mt-6 as-flex as-gap-3 as-wrap">
              <Link to="/" className="as-btn as-btn-primary">
                Go to home
              </Link>
              <Link to="/#conditions" className="as-btn as-btn-secondary">
                See all conditions
              </Link>
            </div>
          </div>
        </Container>
      </main>
    )
  }

  const accentClass = ACCENT_TO_CSS[condition.accent] || ACCENT_TO_CSS.indigo
  const {
    title,
    tagline,
    Icon,
    overview,
    signs,
    whenToReachOut,
    therapyRole,
    medicationRole,
    assessmentSlugs = [],
    faqs = [],
  } = condition

  const openConsultCall = () => onContactClick?.({ mode: 'call' })
  const openConsultWhatsapp = () => onContactClick?.({ mode: 'whatsapp' })

  const assessmentSlug = assessmentSlugs[0]
  const assessmentTarget = assessmentSlug
    ? `/assessment/${assessmentSlug}?src=condition&src-id=${condition.slug}`
    : ''

  const startSelfAssessment = () => {
    if (!assessmentTarget) return
    if (isLoggedIn()) {
      navigate(assessmentTarget)
      return
    }
    const params = new URLSearchParams({
      intent: 'assessment',
      assessmentSlug,
      condition: condition.slug,
      redirect: assessmentTarget,
    })
    navigate(`/signup?${params.toString()}`)
  }

  return (
    <main id="main" className={`cn-page ${accentClass}`}>
      {/* ── Hero ──────────────────────────────────────── */}
      <section className="cn-hero">
        <Container>
          <div className="cn-hero-grid">
            <div className="cn-hero-copy">
              <div className="cn-eyebrow">Specialist care for</div>
              <h1 className="cn-h1">{title}</h1>
              {tagline ? <p className="cn-tagline">{tagline}</p> : null}
              <ul className="cn-hero-points" aria-label="How AUMHUM helps">
                <li><CheckCircle2 /> Therapy, psychiatry, and follow-up in one care plan</li>
                <li><ShieldCheck /> Private, clinician-led support from the first step</li>
              </ul>
              <div className="cn-cta-row">
                <button
                  type="button"
                  className="cn-cta cn-cta-primary cn-cta-large"
                  onClick={openConsultCall}
                >
                  <Stethoscope /> Book an expert consultation
                </button>
                <button
                  type="button"
                  className="cn-cta cn-cta-whatsapp cn-cta-large"
                  onClick={openConsultWhatsapp}
                >
                  <MessageCircle /> Chat on WhatsApp
                </button>
              </div>
              <p className="cn-cta-sub">
                Choose a callback or WhatsApp. We’ll help set an appointment with a
                leading psychiatrist in this condition.
              </p>
            </div>
            <div className="cn-hero-panel">
              <div className="cn-hero-mark" aria-hidden="true">
                <Icon width={46} height={46} />
              </div>
              {assessmentSlugs.length ? (
                <div className="cn-assessment-card">
                  <div className="cn-assessment-icon" aria-hidden="true">
                    <ClipboardList />
                  </div>
                  <div>
                    <div className="cn-section-eyebrow">Self-assessment</div>
                    <h2 className="cn-assessment-title">Start with a private screen</h2>
                    <p className="cn-assessment-copy">
                      A short clinician-validated screener gives you and the care team a
                      clearer starting point before a consultation.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="cn-cta cn-cta-assessment"
                    onClick={startSelfAssessment}
                  >
                    Start the self-assessment <ArrowRight />
                  </button>
                  <div className="cn-assessment-meta">
                    <ShieldCheck /> Sign in required · result saved securely
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </Container>
      </section>

      {/* ── Self-assessment CTA ───────────────────────── */}
      {assessmentSlugs.length ? (
        <section className="cn-section cn-section-assessment">
          <Container>
            <div className="cn-self-test">
              <div>
                <div className="cn-section-eyebrow">Start the self-assessment</div>
                <h2 className="cn-h2">A clear first read, before you speak to us</h2>
                <p className="cn-section-sub">
                  Take a short, confidential questionnaire that helps name the pattern
                  you’re noticing. You’ll sign in or create an account first so the
                  result is saved privately and can be used by the care team.
                </p>
                <ul className="cn-self-test-points">
                  <li><CheckCircle2 /> 3-10 minutes</li>
                  <li><CheckCircle2 /> Clinician-validated</li>
                  <li><CheckCircle2 /> Not a diagnosis</li>
                </ul>
              </div>
              <div className="cn-self-test-actions">
                <button
                  type="button"
                  className="cn-cta cn-cta-assessment cn-cta-full"
                  onClick={startSelfAssessment}
                >
                  Start the self-assessment <ArrowRight />
                </button>
                <div className="cn-self-test-note">
                  Sign in happens before the first question so your answers stay with you.
                </div>
              </div>
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── Overview ──────────────────────────────────── */}
      <section className="cn-section">
        <Container>
          <div className="cn-section-grid">
            <div>
              <div className="cn-section-eyebrow">Overview</div>
              <h2 className="cn-h2">Understanding {title.toLowerCase()}</h2>
            </div>
            <div className="cn-prose">
              {overview.map((p, i) => (
                <p key={`ov-${i}`}>{p}</p>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ── Signs to notice ───────────────────────────── */}
      <section className="cn-section cn-section-alt">
        <Container>
          <div className="cn-section-grid">
            <div>
              <div className="cn-section-eyebrow">Common signs</div>
              <h2 className="cn-h2">What people typically notice</h2>
              <p className="cn-section-sub">
                Not a diagnostic checklist — a map of patterns that often bring people in.
              </p>
            </div>
            <ul className="cn-sign-list" aria-label="Common signs">
              {signs.map((s, i) => (
                <li key={`sign-${i}`}>
                  <span className="cn-check" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                         strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </section>

      {/* ── When to reach out ─────────────────────────── */}
      {whenToReachOut?.length ? (
        <section className="cn-section">
          <Container>
            <div className="cn-section-grid">
              <div>
                <div className="cn-section-eyebrow">When to reach out</div>
                <h2 className="cn-h2">Earlier is always better</h2>
                <p className="cn-section-sub">
                  You don’t need to wait for things to get worse to be entitled to care.
                </p>
              </div>
              <ul className="cn-reach-list">
                {whenToReachOut.map((s, i) => (
                  <li key={`r-${i}`}>
                    <span className="cn-dot" aria-hidden="true" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── Care philosophy: therapy & medication ─────── */}
      <section className="cn-section">
        <Container>
          <div className="cn-philo-head">
            <div className="cn-section-eyebrow">Our clinical approach</div>
            <h2 className="cn-h2">Therapy and medication both have a role</h2>
            <p className="cn-section-sub">
              We’re neutral by design: the plan depends on your presentation, not on
              ideology. Here’s how each contributes.
            </p>
          </div>
          <div className="cn-philo-grid">
            <div className="cn-philo-card">
              <div className="cn-philo-label">Therapy</div>
              <p>{therapyRole}</p>
            </div>
            <div className="cn-philo-card">
              <div className="cn-philo-label">Medication</div>
              <p>{medicationRole}</p>
            </div>
            <div className="cn-philo-card cn-philo-emphasis">
              <div className="cn-philo-label">Medication adherence</div>
              <p>
                When medication is part of the plan, adherence is often the single largest
                factor in long-term outcomes. Our care team checks in on dosing, side-
                effects, and refills — so you’re not alone in managing it.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Care flow visualisation ───────────────────── */}
      <section className="cn-section cn-section-alt">
        <Container>
          <CareFlow
            title="What happens when you reach out"
            sub="No black box — here’s how care unfolds for this condition."
          />
        </Container>
      </section>

      {/* ── FAQ ───────────────────────────────────────── */}
      {faqs.length ? (
        <section className="cn-section">
          <Container>
            <div className="cn-section-grid">
              <div>
                <div className="cn-section-eyebrow">Questions</div>
                <h2 className="cn-h2">Before you book</h2>
              </div>
              <div className="cn-faq">
                {faqs.map((f, i) => (
                  <details key={`faq-${i}`} className="cn-faq-item">
                    <summary>{f.q}</summary>
                    <p>{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </Container>
        </section>
      ) : null}

      {/* ── Final CTA ─────────────────────────────────── */}
      <section className="cn-cta-final">
        <Container>
          <div className="cn-cta-final-card">
            <h2 className="cn-h2">Ready to take the first step?</h2>
            <p>
              Book a call with our care team. We’ll match you with a leading psychiatrist
              in {title.toLowerCase()} and take it from there.
            </p>
            <div className="cn-cta-row">
              <button
                type="button"
                className="cn-cta cn-cta-primary"
                onClick={openConsultCall}
              >
                <PhoneCall /> Book an expert consultation
              </button>
              <button
                type="button"
                className="cn-cta cn-cta-whatsapp"
                onClick={openConsultWhatsapp}
              >
                <MessageCircle /> Chat on WhatsApp
              </button>
            </div>
          </div>
        </Container>
      </section>
    </main>
  )
}
